import { useDataProvider } from 'react-admin';
import { useEffect, useState } from "react";
import InputFieldCustom from '../Commons/InputFieldCustom'
import { useMediaQuery } from '@mui/material';
import BreadCrumbCustom from '../Commons/BreadCrum';
import historyTracker from '../Commons/historyTracker';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import AuthNotification from '../Commons/AuthNotification';
import { Style } from '../Commons/Styles.jsx';
import BottomToolBar from '../Commons/BottomToolBar';
import InternalMenu from '../Commons/InternalMenu';
import CircularProgress from '@mui/material/CircularProgress';
// TODO: reemplazar por el ícono definitivo de Proveedores cuando esté disponible
import providers from '../Commons/Icons/apps.svg';
import providerList from '../Commons/Icons/book.svg';

import { hasPermissions } from '../Commons/CompanyAuth';

// Mismo catalogo fijo que AccountingExpenseCreate.jsx (asistelibros: BA/SA/IP, ver ese
// archivo para el porque no viene de un SP).
const BooksFlagNames = {
    BA: 'Bien Adquirido',
    SA: 'Servicio Adquirido',
    IP: 'Combustible',
};

const formatDate = (value) => value ? new Date(value).toLocaleDateString('es-GT') : '';

const formatMoney = (value) =>
    value === null || value === undefined
        ? ''
        : parseFloat(value).toLocaleString('es-GT', { style: 'decimal', minimumFractionDigits: 2 });

// El "id" de este recurso es compuesto ("corre_facturacion-correlativo", ver comentario en
// Get_Provider_AccountingExpenses/Providersapi.cs): sp_devuelve_cbfacturaintegcontable
// exige ambos valores para ubicar la fila. Se extrae corre_facturacion del propio id en vez
// de depender de location.state/query, porque se llega aca por rowClick="show" desde
// AccountingExpensesList, que no propaga esos datos.
const getInvoiceFromId = (id) => {
    const parts = (id || '').split('-');
    return parts.length === 2 ? parts[0] : null;
};

// Detalle de UN gasto contable (sp_devuelve_cbfacturaintegcontable). Solo lectura, mismo
// patron que ProviderInvoiceShow. Se llega aqui haciendo click en una fila de
// AccountingExpensesList.
const AccountingExpenseShow = () => {
    const { id } = useParams();
    const isSmall = useMediaQuery(theme => theme.breakpoints.down('sm'));
    const navigate = useNavigate();
    const location = useLocation();
    const dataProvider = useDataProvider();
    const invoiceId = getInvoiceFromId(id);

    const [openAuth, setOpenAuth] = useState(false);
    const [auth, setAuth] = useState(false);
    const [invoiceNumber, setInvoiceNumber] = useState('');
    const [costCenter, setCostCenter] = useState('');
    const [account, setAccount] = useState('');
    const [amount, setAmount] = useState('');
    const [vatExemptAmount, setVatExemptAmount] = useState('');
    const [isrExemptAmount, setIsrExemptAmount] = useState('');
    const [isrConceptName, setIsrConceptName] = useState('');
    const [expenseItemName, setExpenseItemName] = useState('');
    const [booksFlag, setBooksFlag] = useState('');
    const [auxiliaryCode, setAuxiliaryCode] = useState('');
    const [auxiliaryDescription, setAuxiliaryDescription] = useState('');
    const [movementDate, setMovementDate] = useState('');
    // Mientras esto es true se muestra el spinner de carga inicial (ver el bloque
    // "initialLoading" en el return): esta pantalla necesita traer permisos y el detalle
    // del gasto contable antes de poder mostrarlo, y sin este estado la ventana se veia
    // en blanco (solo el breadcrumb) durante esa espera.
    const [initialLoading, setInitialLoading] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                if (!invoiceId) {
                    setAuth(false);
                    setOpenAuth(true);
                    return;
                }

                const authdata = { data: hasPermissions('AccountingExpense', 'list') };
                if (authdata.data.length > 0) {
                    setAuth(authdata.data[0]);
                    setOpenAuth(!authdata.data[0]);

                    if (authdata.data[0]) {
                        const [{ data }, invoiceData, expenseItemsData] = await Promise.all([
                            dataProvider.getOne('provider/accountingexpense', { id }),
                            dataProvider.getOne('provider/invoice', { id: invoiceId }).catch(() => ({ data: {} })),
                            dataProvider.getList('provider/expenseitems', { filter: {}, pagination: {}, sort: {} }).catch(() => ({ data: [] })),
                        ]);

                        historyTracker.addPath(location.pathname, 'Gasto Contable', 7);

                        setInvoiceNumber(invoiceData.data?.invoiceNumber || '');
                        setCostCenter(data.costCenterName ? `${data.costCenter} - ${data.costCenterName}` : data.costCenter);
                        setAccount(data.accountName ? `${data.account} - ${data.accountName}` : data.account);
                        setAmount(formatMoney(data.amount));
                        setVatExemptAmount(formatMoney(data.vatExemptAmount));
                        setIsrExemptAmount(formatMoney(data.isrExemptAmount));
                        setIsrConceptName(data.isrConceptName || '');
                        setBooksFlag(BooksFlagNames[data.booksFlag] || data.booksFlag || '');
                        setAuxiliaryCode(data.auxiliaryCode?.toString() || '');
                        setAuxiliaryDescription(data.auxiliaryDescription || '');
                        setMovementDate(formatDate(data.movementDate));

                        if (data.expenseItem) {
                            var match = expenseItemsData.data.find(item => `${item.id}` === `${data.expenseItem}`);
                            setExpenseItemName(match ? match.name : '');
                        }
                    }
                } else {
                    setAuth(false);
                    setOpenAuth(true);
                }
            }
            catch (error) {
                console.log(error);
            }
            finally {
                setInitialLoading(false);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dataProvider, location.pathname, id]);

    const handleRedirectProviderList = () => {
        navigate(`/provider`);
    };

    const [openProviders, setOpenProviders] = useState(true);

    const handleClickOpenProviders = () => {
        setOpenProviders(!openProviders);
    };

    const menus = [{
        icon: <img src={providers} alt="logo" style={Style.MenuLogo} />, name: 'Proveedores',
        open: openProviders,
        openAction: handleClickOpenProviders,
        actions: [
            { icon: <img src={providerList} alt="logo" style={Style.MenuLogo} />, name: 'Listado', onClick: handleRedirectProviderList },
        ]
    }];

    const hasAuxiliaryInfo = !!(auxiliaryCode || auxiliaryDescription);

    return (
        <div class="container">
            <BreadCrumbCustom history={historyTracker.getHistory()} />
            {initialLoading ? (
                // Spinner de carga inicial: reutiliza la misma clase "Loading" (index.css) y
                // el mismo CircularProgress que se usa en otras pantallas para la espera de
                // guardado, pero aqui cubre la espera de auth + detalle del gasto contable,
                // que antes dejaba la pantalla en blanco.
                <div class="Loading" style={{ display: 'block' }}>
                    <CircularProgress size="15rem" />
                </div>
            ) : auth ? (
                <div class="edit">
                    {!isSmall ? (
                        <div class="container">
                            <div class="row">
                                <InputFieldCustom readOnly value={invoiceNumber} setValue={setInvoiceNumber} col="col-3" width="95%" label="Factura" type="text" />
                                <InputFieldCustom readOnly value={costCenter} setValue={setCostCenter} col="col-3" width="95%" label="Centro" type="text" />
                                <InputFieldCustom readOnly value={account} setValue={setAccount} col="col-3" width="95%" label="Cuenta" type="text" />
                            </div>
                            <div class="row">
                                <InputFieldCustom readOnly value={amount} setValue={setAmount} col="col-3" width="95%" label="Monto (Q)" type="text" />
                                <InputFieldCustom readOnly value={vatExemptAmount} setValue={setVatExemptAmount} col="col-3" width="95%" label="Monto No Afecto IVA (Q)" type="text" />
                                <InputFieldCustom readOnly value={isrExemptAmount} setValue={setIsrExemptAmount} col="col-3" width="95%" label="Monto No Afecto ISR (Q)" type="text" />
                                <InputFieldCustom readOnly value={booksFlag} setValue={setBooksFlag} col="col-3" width="95%" label="Clasificación" type="text" />
                            </div>
                            <div class="row">
                                <InputFieldCustom readOnly value={expenseItemName} setValue={setExpenseItemName} col="col-3" width="95%" label="Rubro de Gasto" type="text" />
                                <InputFieldCustom readOnly value={isrConceptName} setValue={setIsrConceptName} col="col-3" width="95%" label="Concepto ISR" type="text" />
                                <InputFieldCustom readOnly value={movementDate} setValue={setMovementDate} col="col-3" width="95%" label="Fecha de Movimiento" type="text" />
                            </div>
                            {hasAuxiliaryInfo ? (
                                <div class="row">
                                    <InputFieldCustom readOnly value={auxiliaryCode} setValue={setAuxiliaryCode} col="col-4" width="95%" label="Auxiliar" type="text" />
                                    <InputFieldCustom readOnly value={auxiliaryDescription} setValue={setAuxiliaryDescription} col="col-8" width="95%" label="Descripción Auxiliar" type="text" />
                                </div>
                            ) : null}

                            <div style={{ marginTop: '2em' }}>
                                <BottomToolBar buttons={[ ]} menu={<InternalMenu menus={menus} />} />
                            </div>
                        </div>
                    ) : (
                        <div class="container">
                        </div>
                    )}
                </div>) : (<AuthNotification openerror={openAuth} severity={'error'} message={'No posee los permisos para poder interactuar con esta vista'} />)
            }
        </div>
    )
}

export default AccountingExpenseShow;
